import axios from 'axios';
import type { AxiosRequestConfig } from 'axios';
import { handleResponse } from './response';

import { useMock } from './mock';

if (!import.meta.env.MODE.startsWith('production')) useMock(axios);

const getRequestUrl = (resource: string, id?: string) => {
	const url = `/${resource}`;
	return id ? url + `/${id}` : url;
};

const getList = async <T = any>(
	resource: string,
	config?: AxiosRequestConfig,
) => {
	const response = await axios.get<T>(getRequestUrl(resource), config);
	return handleResponse<T>(response);
};

const getSingle = async <T = any>(
	resource: string,
	id: string,
	config?: AxiosRequestConfig,
) => {
	const response = await axios.get<T>(getRequestUrl(resource, id), config);
	return handleResponse<T>(response);
};

const post = async <T = any>(
	resource: string,
	model: Record<string, any>,
	config?: AxiosRequestConfig,
) => {
	const response = await axios.post<T>(getRequestUrl(resource), model, config);
	return handleResponse<T>(response);
};

const put = async <T = any>(
	resource: string,
	id: string | undefined,
	model: Record<string, any>,
	config?: AxiosRequestConfig,
) => {
	const response = await axios.put<T>(getRequestUrl(resource, id), model, config);
	return handleResponse<T>(response);
};

const patch = async <T = any>(
	resource: string,
	id: string,
	model?: Record<string, any>,
	config?: AxiosRequestConfig,
) => {
	const response = await axios.patch<T>(getRequestUrl(resource, id), model, config);
	return handleResponse<T>(response);
};

const patchWithoutId = async <T = any>(
	resource: string,
	model?: Record<string, any>,
	config?: AxiosRequestConfig,
) => {
	const response = await axios.patch<T>(getRequestUrl(resource), model, config);
	return handleResponse<T>(response);
};

const patchMany = async <T = any>(
	resource: string,
	model: Record<string, any>,
	config?: AxiosRequestConfig,
) => {
	const response = await axios.patch<T>(getRequestUrl(resource), model, config);
	return handleResponse<T>(response);
};

const remove = async <T = null>(
	resource: string,
	id: string,
	config?: AxiosRequestConfig,
) => {
	const response = await axios.delete<T>(getRequestUrl(resource, id), config);
	return handleResponse<T>(response);
};

const removeMany = async <T = any>(
	resource: string,
	config?: AxiosRequestConfig,
) => {
	const response = await axios.delete<T>(getRequestUrl(resource), config);
	return handleResponse<T>(response);
};

export {
	getList,
	getSingle,
	post,
	put,
	patch,
	patchMany,
	patchWithoutId,
	remove,
	removeMany,
};
